import type { ReferencedMultiChartDataPoint, TimeSeriesChartPoint } from "@wayflyer/flyui-dataviz";

import type { DashboardOutput } from "../../utils/fetch-api";
import type { CustomTimeRange } from "../../utils/types";
import { MetricTypes } from "../../autogen/metrics";
import { formatValue } from "../../strings/type-formatting";
import { includeMetricDecimal } from "../../utils/metrics";
import { getReferenceData } from "../../overview/overview-datapoints-2";
import type { SummaryPanelItemProps } from "../../components/summary-panel";
import { MetricReferenceDirection } from "../../components/shared";

import type { TikTokAccountData } from "./tiktok-spend";

export const getGroupNameWithId = (group: string | null, accountsData: TikTokAccountData | null) => {
  if (!group) {
    return "";
  }
  if (group === "Total") {
    return group;
  }
  const name = accountsData?.[group]?.[MetricTypes.TIKTOK_ACCOUNT_NAME];
  return name ? `${name} (${group})` : group;
};

export interface TikTokSummaryItem extends SummaryPanelItemProps {
  rawMetricValue?: number | null;
  currencyCode?: string;
  fractionDigits?: number;
  trendValue?: number;
}

export const getTikTokLinePoints = (
  data: ReferencedMultiChartDataPoint[],
  group: string,
  period: "Current" | "Reference"
): TimeSeriesChartPoint[] =>
  data.map((row) => {
    const rawValue = row[`${group}${MetricTypes.TIKTOK_AD_SPEND}${period}`];
    const value = rawValue == null || rawValue === "" ? null : Number(rawValue);
    return {
      x: Date.parse(`${row.dayRaw}T00:00:00Z`),
      y: value != null && Number.isFinite(value) ? value : null,
      datum: row,
    };
  });

export const aggregateTikTokSummaryData = (
  current: DashboardOutput,
  reference: DashboardOutput,
  timeRange: CustomTimeRange,
  tabs: SummaryPanelItemProps[],
  accountsData: TikTokAccountData,
  groups: string[]
) => {
  const deriveMetric = (metricType, data) => {
    return data?.[metricType] || "";
  };

  return groups.reduce((result, group) => {
    const ts = tabs.map((tab) => {
      const metric = current.metrics.find((metric) => metric.name === tab.metricType);

      const metricValue = deriveMetric(group + tab.metricType, current.summary);
      const rawValue = metricValue === "" || metricValue == null ? null : Number(metricValue);
      const rawMetricValue = rawValue != null && Number.isFinite(rawValue) ? rawValue : null;
      const newTab: TikTokSummaryItem = {
        tooltip: tab.tooltip,
        metricType: group,
        title: getGroupNameWithId(group, accountsData),
        rawMetricValue,
        currencyCode: metric?.type === "CURRENCY" ? metric.currencyCode : current.currencyCode,
        fractionDigits:
          rawMetricValue != null &&
          Math.abs(rawMetricValue) < 100 &&
          includeMetricDecimal(tab.metricType as MetricTypes)
            ? 2
            : 0,
      };

      newTab["metricValue"] =
        metricValue == null
          ? null
          : formatValue({
              value: metricValue.toString(),
              metricType: metric,
              withDecimal: includeMetricDecimal(tab.metricType as MetricTypes),
            });
      const metricReferenceValue = deriveMetric(group + tab.metricType, reference.summary);
      // Percentage change is undefined when the reference is zero.
      if (Number(metricReferenceValue) === 0) {
        return newTab;
      }
      newTab["metricReferenceValue"] =
        metricReferenceValue == null
          ? null
          : formatValue({
              value: metricReferenceValue.toString(),
              metricType: metric,
              withDecimal: includeMetricDecimal(tab.metricType as MetricTypes),
            });
      const referenceData = getReferenceData(metricValue, metricReferenceValue, metric?.success_direction);
      if (referenceData) {
        newTab["metricReferenceSuccess"] = referenceData.metricReferenceSuccess;
        newTab["metricReferenceDirection"] = referenceData.metricReferenceDirection;
        newTab["metricReferenceValue"] = referenceData.metricReferenceValue;
        const delta = ((Number(metricValue) - Number(metricReferenceValue)) * 100) / Number(metricReferenceValue);
        newTab.trendValue =
          referenceData.metricReferenceDirection === MetricReferenceDirection.SAME
            ? 0
            : Number.isFinite(delta)
            ? delta
            : Math.sign(delta);
      }
      return newTab;
    });

    return [...result, ...ts];
  }, [] as TikTokSummaryItem[]);
};
